import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelection } from '@/contexts/SelectionContext';
import { useJourney } from '@/hooks/useJourney';
import { FeedSwitcher } from '@/components/context/FeedSwitcher';
import { JourneyChips } from '@/components/JourneyChips';
import { Skeleton } from '@/components/ui/skeleton';
import type { JourneyStep } from '@/lib/journey';
import { chipClickFor } from '@/lib/journeyTargets';

/**
 * Baut einen stabilen Inhalts-Schluessel aus den Journey-Schritten statt der
 * Array-Identitaet zu vergleichen: `useJourney` liefert bei JEDEM Render ein
 * frisches Array (`deriveJourney` baut es neu), auch wenn sich Zustand/Zahlen
 * gar nicht geaendert haben. Ein Identitaets-Vergleich wuerde den Fit-Effekt
 * unten bei jedem Render neu feuern, ein Inhalts-Schluessel nur dann, wenn
 * sich wirklich etwas Sichtbares geaendert hat.
 */
function journeyContentKey(steps: JourneyStep[]): string {
  // Seit dem Umbau auf sechs Schritte (Statusfluss, 16.09.2026) traegt
  // `JourneyStep` kein `secondary`/`rejected` mehr, sondern ein typisiertes
  // `detail` je Schrittart (`JourneyStepDetail` in `lib/journey.ts`). Der
  // Inhalts-Schluessel muss weiterhin JEDE sichtbare Aenderung erfassen —
  // `JSON.stringify` deckt alle Felder eines `detail`-Objekts ab, ohne sie
  // hier einzeln aufzaehlen und bei der naechsten `detail`-Erweiterung
  // erneut nachziehen zu muessen.
  return steps.map((s) => `${s.id}:${s.state}:${s.count}:${JSON.stringify(s.detail)}`).join('|');
}

/**
 * Misst die Breite eines Containers per `ResizeObserver` — als CALLBACK-Ref,
 * nicht als `useLayoutEffect(fn, [])` (Muster `useStickyActionBar.ts`, dort
 * aber ein IMMER gemountetes Element). Der Unterschied ist hier tragend:
 * `ContextJourneyBand` gibt bei fehlendem `merchantCenter` `null` zurueck,
 * das gemessene `<div>` existiert also beim allerersten Render dieser
 * Komponente noch gar nicht — `merchantCenter` loest sich in
 * `SelectionContext` asynchron auf. Ein `useLayoutEffect(fn, [])` feuert
 * NUR nach dem ersten Commit DIESER Komponenten-Instanz; ist `ref.current`
 * zu dem Zeitpunkt `null` (weil das `<div>` noch nicht existiert), kehrt der
 * Effekt fruehzeitig zurueck und feuert wegen des leeren Deps-Arrays NIE
 * WIEDER — auch nicht, wenn das `<div>` Renders spaeter tatsaechlich
 * montiert. `width` blieb dann fuer die gesamte Lebensdauer der Komponente
 * `0`, real also IMMER `variant="micro"` (Review-Fund Fix-Runde 1).
 *
 * Eine Callback-Ref feuert dagegen bei JEDEM Mount/Unmount DES KONKRETEN
 * DOM-KNOTENS, unabhaengig davon, in welchem Render-Zyklus der Komponente
 * das passiert — genau das deckt den Fall "`<div>` erscheint erst spaeter"
 * ab. Der Observer selbst liegt in einer `useRef`, damit ein spaeterer
 * Knoten-Wechsel (merchantCenter verschwindet -> `<div>` weg -> erscheint
 * wieder) den alten Observer sauber trennt, statt an einem entfernten
 * Knoten haengen zu bleiben.
 */
function useMeasuredWidth<T extends HTMLElement>() {
  const [width, setWidth] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: T | null) => {
    // Immer zuerst trennen: deckt sowohl das Unmounten (node === null) als
    // auch einen Knoten-Wechsel ab (React ruft die Callback-Ref mit null,
    // dann mit dem neuen Knoten) — ohne das haengt der alte Observer am
    // laengst entfernten Knoten weiter (Speicherleck, keine neue Messung).
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!node) return;

    const measure = () => setWidth(node.clientWidth);
    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return { ref, width };
}

/**
 * Zweiter, UNABHAENGIGER Ausloeser fuer Effekt 2 (die Vorwaerts-Pruefung
 * "passt die aktuell gerenderte Stufe?") -- er beobachtet NICHT die
 * Bandbreite (das tut `useMeasuredWidth` oben schon), sondern die
 * tatsaechliche Box-Groesse des Mess-Slots (`chipsWrapRef`/`journey-slot`)
 * SELBST.
 *
 * Anlass: Produktions-Messung 07./08.09.2026, 84 Einzelmessungen ueber
 * sechs Breiten und zwei Feeds -- 19 von 48 frischen Ladevorgaengen
 * unterhalb 1280px zeigten eine gerenderte, ABGESCHNITTENE Journey (DOM
 * "3.778 22 20 offen 1" mit sichtbar fehlender "1" von "Live 1"; bei
 * golfimport riss es sogar bei 1280px mit der VOLLEN Variante). Dieselbe
 * Kombination aus Feed und Fensterbreite lieferte bei Wiederholung beide
 * Ergebnisse -- ein Rennen, kein Breitenproblem.
 *
 * Ursache: das Herunterstufen (full -> micro) aendert die natuerliche
 * Breite des Slot-INHALTS, damit die Flex-Verteilung zwischen den
 * Geschwistern in der Leiste (Slot und `FeedSwitcher`-Pille), damit
 * moeglicherweise die tatsaechlich verfuegbare Breite des Slots selbst --
 * OHNE dass sich die Breite des AEUSSEREN Bandes dabei aendern muss (die
 * Leiste in `Layout.tsx` kann dem Band insgesamt eine unveraenderte Breite
 * zuteilen, waehrend sich nur die interne Aufteilung zwischen Pille und
 * Slot verschiebt -- z.B. weil die Zeiger-Pille durch neu eingetroffene
 * Feed-Zahlen selbst breiter oder schmaler wird). Effekt 1 (Reset auf
 * `full`) haengt an der BAND-Breite; Effekt 2 mass bisher nur, WENN Effekt
 * 1 oder ein Inhalts-/Font-Wechsel es anstiess -- ein reiner
 * Geschwister-Reflow des Slots selbst loeste bis hierhin gar nichts aus,
 * die zuletzt gerenderte (moeglicherweise laengst zu breite) Stufe blieb
 * einfach stehen und der Browser schnitt den Ueberlauf ab.
 *
 * Ein frueherer Anlauf hat einen ResizeObserver auf dem Slot ausdruecklich
 * ABGELEHNT ("der Slot ist `overflow-hidden`, sein Boxmodell bleibt vom
 * Flex-Layout bestimmt") -- das war der Denkfehler, nicht die Begruendung
 * fuer den Verzicht: GENAU WEIL das Flex-Layout die Box-Groesse des Slots
 * bestimmt, AENDERT sie sich bei jedem Reflow eines Geschwisters, und genau
 * darauf muss reagiert werden, nicht trotzdem darauf verzichtet.
 *
 * Der Hook bumpt bewusst nur einen ZAEHLER (keinen Breitenwert) -- die
 * eigentliche Messung bleibt der direkte `scrollWidth`/`clientWidth`-Read
 * in Effekt 2 gegen den echten DOM-Knoten, dieser Zaehler ist nur der
 * zusaetzliche Ausloeser, WANN dieser Read erneut passieren soll.
 *
 * Bewusst NICHT als zusaetzliche Dependency von Effekt 1 (Reset auf
 * `full`): der Slot resized sich unter anderem GENAU DESHALB, weil Effekt 2
 * gerade herabgestuft hat -- haenge Effekt 1 ebenfalls daran, setzt jede
 * Herabstufung sich selbst sofort wieder auf `full` zurueck und die Kette
 * oszilliert endlos. Effekt 1 bleibt darum ausschliesslich an der
 * AEUSSEREN Bandbreite, dem Inhalts-Schluessel und dem Font-Tick haengen;
 * Effekt 2 ist die einzige Stelle, die auch auf den Slot selbst reagiert,
 * und seine Kette laeuft ohnehin nur vorwaerts (`full` -> `micro` ->
 * `hidden`) und terminiert spaetestens bei `hidden` (dann existiert der
 * beobachtete Knoten nicht mehr, der Observer trennt sich beim Unmount).
 *
 * Callback-Ref aus demselben Grund wie `useMeasuredWidth` oben (bindet an
 * den konkreten DOM-Knoten, nicht an den Render-Zyklus der Komponente);
 * `nodeRef` wird zusaetzlich nach aussen gereicht, damit Effekt 2 weiterhin
 * per `.current` direkt lesen kann, ohne einen zweiten, aus dem Tritt
 * geratenen Zustand mitzufuehren.
 */
function useSlotResizeTick<T extends HTMLElement>() {
  const nodeRef = useRef<T | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [tick, setTick] = useState(0);

  const ref = useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    nodeRef.current = node;

    if (!node) return;
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => setTick((n) => n + 1));
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return { ref, nodeRef, tick };
}

/**
 * Die eine Zeile INNERHALB der 56-px-Header-Leiste statt eines eigenen
 * Bandes darunter (Spec docs/superpowers/specs/2026-09-07-app-entlastung-
 * design.md, Abschnitt 5.1, R1): Kontext-Pille (`FeedSwitcher`) links, der
 * gemessene Nutzerpfad (`JourneyChips`) daneben. Diese Komponente traegt
 * seitdem KEINE eigene Umrandung, keinen eigenen Hintergrund und kein
 * eigenes `print:hidden` mehr — das alles liegt jetzt auf der Header-Leiste
 * in `Layout.tsx`, die diese Komponente direkt als Kind rendert.
 *
 * Vorgeschichte (Spec docs/superpowers/specs/2026-08-18-eine-zeile-feed-
 * switcher-design.md): ersetzte die vorherigen zwei Baender in `Layout.tsx`
 * (Kontextleiste + JourneyBar), die beide `merchantCenter` bzw.
 * `selectedFeedId` einzeln abfragten und deshalb bei schmalen
 * Sidebars/Fenstern gegeneinander umbrachen. Die Breite entscheidet hier EIN
 * Container statt zweier unabhaengiger Bloecke.
 *
 * Fit-basierte Variante statt fester Schwelle: eine feste Breiten-Schwelle
 * (vormals `FULL_VARIANT_MIN_WIDTH = 980`) mass nur das Band, nicht ob die
 * volle Variante wirklich neben der Kontext-Pille Platz findet — die
 * Zeiger-Pille traegt live gemessene Zahlen ("0 bereit zum Hochladen | 4.921
 * warten auf Freigabe") und ihre Breite schwankt je nach Feed und Tageszeit.
 * `full` wird deshalb einfach VERSUCHT und per `scrollWidth`/`clientWidth`-
 * Vergleich auf `micro` heruntergestuft, wenn es nicht passt —
 * `useLayoutEffect` macht die Herabstufung unsichtbar (laeuft vor dem
 * Paint).
 *
 * **Der `stacked`-Zweig (Umbruch auf zwei Zeilen unter 640px Bandbreite)
 * ist mit R1 ersatzlos entfallen:** in einer 56-px-Leiste gibt es keine
 * zweite Zeile, dafuer fehlt schlicht die Hoehe. Den schmalen Fall traegt
 * seitdem allein die bestehende Herunterstufung `full` -> `micro` — die
 * Mikro-Variante ist genau fuer diesen Fall gebaut (nur Zahlen, Woerter im
 * Tooltip) und braucht keine zweite Zeile, um zu passen. `useMeasuredWidth`
 * (Band-Breite) bleibt trotzdem bestehen: `width` ist die Abhaengigkeit,
 * an der der erneute `full`-Versuch bei einer Groessenaenderung haengt
 * (Effekt 1 unten) — ohne sie bliebe eine einmal heruntergestufte
 * Mikro-Variante fuer immer micro, auch wenn das Fenster waechst.
 *
 * **Dritte Stufe `hidden` (Produktions-Verifikation 08.09.2026):** `full` ->
 * `micro` war nicht das Ende der Kette. Gemessen auf app.feedoptimizer.ai
 * bei 1024px passte selbst die Mikro-Variante nicht neben eine breite
 * Kontext-Pille (Mikro braucht 192px, bekommt 121) — der Browser schnitt den
 * UEBERLAUFENDEN Rest der letzten Chips einfach ab, WEIL der Slot zwar
 * `overflow-hidden` traegt, aber nach der Herabstufung nie erneut prueft, ob
 * die (kuerzere, aber nicht beliebig kurze) Mikro-Variante selbst noch
 * passt. Sichtbares Ergebnis: aus "20 offen" wurde eine abgeschnittene "2",
 * "Live 1" fiel ganz weg — eine FALSCHE Zahl statt einer fehlenden. Eine
 * abgeschnittene Zahl ist keine reduzierte Information, sie ist eine
 * erfundene. Weglassen ist ehrlich, Abschneiden nicht — und der Nutzer
 * verliert dabei nichts Unwiederbringliches, dieselben Zahlen stehen auf dem
 * Feed-Check. Deshalb dieselbe Pruefung ein zweites Mal: passt auch `micro`
 * nicht in den Slot, wird die Journey GAR NICHT gerendert (`attempt ===
 * 'hidden'`, siehe die Bedingung im JSX unten). Die Kette laeuft nur
 * VORWAERTS (`full` -> `micro` -> `hidden`) und wird ausschliesslich von
 * Effekt 1 (Breiten-/Inhaltsaenderung) wieder auf `full` zurueckgesetzt —
 * keine Oszillation zwischen den drei Stufen.
 *
 * **Erneute Messung bei reiner Inhaltsverbreiterung (Befund 4, zweiter
 * Teil):** bei ~1280px passte die volle Variante ebenfalls nicht (Bedarf
 * 400px, Slot 377px), stufte aber nicht auf `micro` herab. Effekt 2 haengt
 * an `width` (Bandbreite) und `contentKey` (Journey-Zahlen) — beide aendern
 * sich nicht, wenn die WebFont nachlaedt und dieselben Ziffern/Woerter
 * schlicht breiter rendert als die Fallback-Schrift zuvor. Der Slot bleibt
 * gleich breit, der Inhalt wird breiter, niemand misst nach. Fix: sobald
 * `document.fonts.ready` aufloest, wird ein Zaehler hochgezaehlt, der in
 * BEIDEN Effekten als zusaetzliche Abhaengigkeit steht — das startet die
 * gesamte Kette (zurueck auf `full`, dann erneut pruefen) ein einziges Mal
 * nach dem Font-Swap neu, ohne einen fortlaufenden ResizeObserver auf dem
 * Inhalt selbst: der Slot ist `overflow-hidden` und sein eigenes
 * Boxmodell aendert sich beim Font-Swap nicht (es wird ja weiterhin vom
 * Flex-Layout auf die verfuegbare Breite gezwungen) — ein ResizeObserver
 * DARAUF wuerde also gar nicht feuern. `document.fonts` fehlt in manchen
 * Umgebungen (jsdom kennt es nicht) — entsprechend defensiv abgefragt.
 *
 * **Messung 16.09.2026 (Platzbilanz, sechs Chips).** Gemessen im echten
 * Browser gegen den Dev-Server mit der Produktions-Datenbank (Testkonto
 * `dennisgerich.sea@`, Feed golfimport CH-DE, alle sechs Zustaende
 * besetzt), nicht geschaetzt (Hausregel aus `FeedSwitcher.tsx`: bei engen
 * Leisten je Breite den Bedarf JEDES Elements messen):
 *
 *     Fensterbreite   Bandbreite   Kontext-Pille   Budget Slot   Ergebnis
 *     1024 px             473 px          368 px         93 px   hidden
 *     1280 px             729 px          368 px        349 px   micro (193 px)
 *     1440 px             889 px          368 px        509 px   micro (193 px)
 *     1920 px            1369 px          368 px        989 px   full  (882 px)
 *
 * Zwei Zahlen tragen die ganze Bilanz: die volle Fassung braucht mit sechs
 * Chips **882 px**, und die Kontext-Pille nimmt in JEDER Breite **368 px**
 * vorweg. `full` passt damit erst ab rund 1900 px -- vorher, mit vier
 * Chips, lag diese Grenze deutlich tiefer.
 *
 * Die Bruchstellen selbst sind unveraendert (die Kette misst, sie rechnet
 * nicht mit festen Schwellen). Geaendert wurde die MIKRO-Fassung: sie trug
 * zuerst an jedem der sechs Punkte einen Kurztext und brauchte damit mehr
 * als die 509 px bei 1440 px -- gemessen fiel die Leiste auf jedem
 * geprueften Fenster unter 1920 px auf `hidden`, also auf gar keine Leiter.
 * Das waere schlechter als die vier Punkte davor gewesen, und die Spec
 * erlaubt `hidden` ausdruecklich nur unter 1024 px. Seit nur noch der
 * Zeiger seinen Text traegt (siehe `JourneyChips`), misst `micro` 193 px
 * und steht ab 1280 px wieder da.
 */
export function ContextJourneyBand() {
  const { merchantCenter, selectedFeedId } = useSelection();
  const journey = useJourney();
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const location = useLocation();
  const [switcherOpenRequest, setSwitcherOpenRequest] = useState(0);
  const { ref, width } = useMeasuredWidth<HTMLDivElement>();
  const { ref: slotRef, nodeRef: chipsWrapRef, tick: slotResizeTick } = useSlotResizeTick<HTMLDivElement>();
  const [attempt, setAttempt] = useState<'full' | 'micro' | 'hidden'>('full');
  const [fontsReadyTick, setFontsReadyTick] = useState(0);
  const contentKey = journeyContentKey(journey.steps);

  // Reagiert GENAU einmal auf den WebFont-Swap: `document.fonts.ready`
  // loest auf, sobald alle beim Laden angeforderten Fonts geladen sind (oder
  // sofort, wenn es keine gibt). Bis dahin hat der Browser mit der
  // Fallback-Schrift gerechnet, die je nach Ziffern/Woertern schmaler sein
  // kann als die echte Schrift — die Chips wachsen dann NACH der ersten
  // Messung, ohne dass sich Bandbreite oder Journey-Zahlen aendern.
  useEffect(() => {
    if (typeof document === 'undefined' || !('fonts' in document)) return;
    let cancelled = false;
    document.fonts.ready
      .then(() => {
        if (!cancelled) setFontsReadyTick((n) => n + 1);
      })
      .catch(() => {
        // document.fonts.ready lehnt in der Praxis nicht ab -- best-effort,
        // ein Fehler hier darf die Journey nie zum Absturz bringen.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Effekt 1: bei jeder Breiten-, Inhalts- oder Font-Aenderung erst wieder
  // `full` VERSUCHEN — sonst bliebe eine einmal heruntergestufte Variante
  // fuer immer so stehen, auch wenn das Fenster waechst, die Zeiger-Pille
  // kuerzer wird oder die echte Schrift schmaler ist als der Fallback.
  // `contentKey` statt `journey.steps` als Dependency: die Zahlen sind das,
  // was die Breite der Zeiger-Pille tatsaechlich aendert, nicht die
  // Array-Referenz.
  useLayoutEffect(() => {
    setAttempt('full');
    // Bewusst nur width + contentKey + fontsReadyTick: `setAttempt` selbst
    // ist stabil, sie gehoert nicht in die Dependency-Liste eines Effekts,
    // der sie aufruft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, contentKey, fontsReadyTick]);

  // Effekt 2: nach jedem Versuch (durch Effekt 1, den Erstrender ODER
  // einen reinen Reflow des Slots selbst, siehe `slotResizeTick` /
  // `useSlotResizeTick` oben) messen, ob die AKTUELL gerenderte Variante
  // tatsaechlich in den Slot passt. `slotResizeTick` schliesst das
  // Renn-Fenster aus der Produktionsmessung 07./08.09.2026 (19 von 48
  // Ladevorgaengen unterhalb 1280px zeigten eine abgeschnittene Journey):
  // ohne diese Dependency lief diese Pruefung nur, wenn die AEUSSERE
  // Bandbreite, der Inhalts-Schluessel oder der Font-Tick sich aenderten —
  // ein reiner Geschwister-Reflow (die `FeedSwitcher`-Pille wird breiter/
  // schmaler, waehrend das Band insgesamt gleich breit bleibt) loeste
  // bis dahin gar keine erneute Messung aus, die zuletzt gerenderte Stufe
  // blieb stehen und der Browser schnitt den Ueberlauf ab. Kette laeuft
  // nur in eine Richtung, `full` -> `micro` -> `hidden`: faellt in diesem
  // Durchlauf bereits `hidden`, tut dieser Effekt nichts mehr (es gibt dann
  // auch keinen `chipsWrapRef.current` mehr, siehe die Render-Bedingung
  // unten) — keine Oszillation. `slotResizeTick` ist bewusst NICHT auch
  // Dependency von Effekt 1 (siehe Kopfkommentar von `useSlotResizeTick`) —
  // sonst wuerde jede Herabstufung, die den Slot selbst resized, sich
  // ueber Effekt 1 sofort wieder auf `full` zuruecksetzen.
  useLayoutEffect(() => {
    if (attempt === 'hidden') return;
    const el = chipsWrapRef.current;
    if (!el) return;
    if (el.scrollWidth > el.clientWidth + 1) {
      setAttempt((prev) => (prev === 'full' ? 'micro' : 'hidden'));
    }
  }, [attempt, width, contentKey, fontsReadyTick, slotResizeTick]);

  // Bedingung ist `merchantCenter`, nicht `selectedFeedId`: die Kontext-Pille
  // ist genau dann sinnvoll, wenn es ueberhaupt etwas auszuwaehlen gibt. Im
  // Erstkontakt (kein Merchant Center, Assistent offen) waere ein leeres Band
  // ein Angebot ueber eine gemessene Null.
  if (!merchantCenter) return null;

  return (
    <div ref={ref} data-testid="context-journey-band" className="flex min-w-0 flex-1 items-center gap-3">
      <FeedSwitcher openRequest={switcherOpenRequest} />
      {selectedFeedId &&
        (journey.isError ? (
          // A2-NEU-2: gescheiterte Zaehlung ist kein Ladezustand. Ein Skeleton,
          // das nie endet, behauptet Arbeit, die nicht mehr stattfindet.
          <span data-testid="journey-failed" role="status" className="text-xs text-muted-foreground">
            {t('journey.countsFailed')}
          </span>
        ) : journey.isLoading ? (
          // Ehrlicher Ladezustand statt einer Leiste, die gleich kippt (Muster
          // FeedHealthDashboard cta-pending, siehe useJourney.isLoading): beim
          // Kaltstart steht `selectedFeedId` synchron aus dem localStorage,
          // die Zahlen kommen erst danach.
          <Skeleton data-testid="journey-pending" className="h-[30px] w-64 rounded-full" />
        ) : attempt === 'hidden' ? (
          // Weder `full` noch `micro` passt neben die Kontext-Pille — dann
          // wird NICHTS gerendert, kein abgeschnittener Rest. Siehe die
          // ausfuehrliche Begruendung im Kopfkommentar der Komponente
          // ("Dritte Stufe `hidden`"): eine abgeschnittene Zahl ist eine
          // FALSCHE Zahl, keine reduzierte Information.
          null
        ) : (
          // `overflow-hidden` macht diesen Knoten zum Mess-Container: sein
          // `scrollWidth` bleibt die volle (nicht gestauchte) Breite der
          // Chips, sein `clientWidth` der tatsaechlich verfuegbare Platz.
          // KEIN `justify-end` (Final-Review-Fund F5): der Slot hat kein
          // `flex-1` mehr und ist damit inhaltsbreit, `justify-end` haette
          // hier keine Wirkung -- ausser im Ueberlauf-Moment zwischen den
          // beiden Effekten oben, wo es zusammen mit `overflow-hidden` den
          // LINKEN statt den rechten Rand abschneidet und damit die ERSTEN
          // Punkte verschwinden liesse statt der letzten. `min-w-0` bleibt,
          // es traegt die Messung (macht den Flex-Item schrumpfbar unter
          // seinen Inhalt).
          <div ref={slotRef} data-testid="journey-slot" className="flex min-w-0 overflow-hidden">
            <JourneyChips
              steps={journey.steps}
              nextStep={journey.nextStep}
              variant={attempt}
              targets={journey.targets}
              pathname={location.pathname}
              onStepClick={(id) => {
                // A3-09 (Option 3A): erledigte Schritte und der Feed-Schritt
                // auf der Startseite haben ein eigenes Ziel, siehe chipClickFor.
                const step = journey.steps.find((s) => s.id === id);
                const click = step
                  ? chipClickFor(step, journey.targets, location.pathname)
                  : { kind: 'navigate' as const, to: journey.targets[id] };
                if (click.kind === 'open-switcher') setSwitcherOpenRequest((n) => n + 1);
                else navigate(click.to);
              }}
            />
          </div>
        ))}
    </div>
  );
}
